import { ApolloQueryResult, QueryResult } from '@apollo/client';
import Button, { LoadingButton } from '@atlaskit/button';
import Checkbox from '@atlaskit/checkbox';
import { ErrorMessage, Field, FieldProps, FormSection } from '@atlaskit/form';
import CheckIcon from '@atlaskit/icon/glyph/check';
import EditorAddIcon from '@atlaskit/icon/glyph/editor/add';
import InfoIcon from '@atlaskit/icon/glyph/info';
import SectionMessage from '@atlaskit/section-message';
import Select, { ValueType } from '@atlaskit/select';
import Textfield from '@atlaskit/textfield';
import Toggle from '@atlaskit/toggle';
import Tooltip from '@atlaskit/tooltip';
import { Currency, PricingPlanCycle } from '@atlassian/cssxp-ccp-types';
import { abgGraphqlClient as graphqlClient } from '@atlassian/temp-client-utils';
import { InvoiceGroup, Maybe, Offering, Promotion } from '@css-xp/graphtypes';
import {
  BillingFrequency,
  EntitlementDetails,
  EntitlementTermDetails,
  FetchAvailablePricingPlansQuery,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  PaymentMethodType,
  PricingPlan,
  Promotion as PromotionV2,
  TransactionAccount,
} from '@css-xp/graphtypes/src/generated-types-new';
import { getAppropriatePromotions } from '@atlassian/temp-helper';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { getBillEstimate, termPaymentVerification, updateInvoiceGroup } from '@atlassian/temp-proxies';
import { EntitlementDefaultsResponse, OrderItemType } from '@atlassian/cssxp-types-entitlement';
import {
  CustomOrderRequest,
  EndsAtType,
  NEW_DATE_FORMAT,
  OrderIntentRequest,
  PricingPlanType,
  PromotionInstance,
  UpdateOrderRequest,
} from '@atlassian/cssxp-typescommon';
import {
  entitlementHasDefaultPricingPlans,
  getCorrectedCurrentDateForAnnual,
  getDefaultPricingPlan,
  getProductMaxUserTier,
  getUserTier,
  getUserTierOptions,
  isFreeOfferingEntitlement,
  isFreePlanEntitlement,
  MARKETPLACE_PROMO_CODE,
} from '@atlassian/cssxp-utils-entitlement';
import {
  ENABLE_ANNUAL_TERMS,
  ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE,
  ENABLE_ENTITLEMENT_SKIP_TRIAL,
  ENABLE_MANAGE_SUBSCRIPTION_BULK_EDIT,
  ENABLE_PARTNER_PROMOTION,
  ENABLE_PRICING_PLAN_CHANGE,
  ENABLE_VIEW_PRICING_DETAILS_ON_MANAGE,
  ENABLE_CREDIT_MANAGEMENT,
} from '@atlassian/cssxp-utils-generic';
import { isAtlassianAccess, isJSMProduct, isStatuspage } from '@atlassian/cssxp-utils-product';
import {
  DISCRETIONARY_DISCOUNT,
  DismissableInlineMessage,
  FormattedDate,
  InlineMessage,
  InvoiceGroupFormField,
  InvoiceGroupSelectItem,
  isDiscretionaryDiscountPromotion,
  PromotionItemDetail,
  SummarySectionV2,
  useFeatureToggle,
  withApollo,
} from '@css-xp/base';
import { getParentContainerId, LinkedEntitlementsTable } from '@css-xp/base/entitlement-utils';
import { SelectPromotionFormFieldV2 } from '@css-xp/base/forms';
import { PartnerPromotionItem } from '@css-xp/base/partner-promotion-item';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { capitalize, defaultTo, get, omit } from 'lodash';
import moment from 'moment';
import React, { FormEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchAvailableOfferingsForEntitlement } from '../../../../entitlement-page/src/queries/fetch-available-offerings-for-entitlement';
import { ChangePricePlan } from '../../../components/ChangePricePlan';
import {
  fetchPricingPlans,
  getDefaultAPIEntitlementData,
  getIntendedBillingPeriodState,
  getPartnerPromotionQuery,
} from '../../manage-entitlement/src/manage-entitlement-helper';
import { ProductFamilyTable, ProductFamilyTableV2 } from '../../manage-entitlement/src/review-change';
import { PriceDifference } from '../../manage-entitlement/src/review-change/price-difference';
import {
  Estimate,
  FormData,
  FormState,
  newAddedPromotionValueResolver,
  TermPaymentValidation,
} from './entitlement-cancel-active-subscription-pane';
import './entitlement-cancel-active-subscription-pane.scss';
import { creditPolicyResponse, txaPushPaymentStatus } from '@atlassian/cssxp-types-billing';
import { FinancePolicyReasonCode } from '../../manage-entitlement/src/manage-billing-profile';

interface OfferingSelectItem {
  label: string;
  value: {
    id: string;
  };
}

type PORequest = {
  loading: boolean;
  data: any;
  done: boolean;
  error: any;
};

type BillingAnchorResponse = {
  response?: any;
  error?: any;
  loading?: boolean;
};

type PartnerPromotionType = {
  error?: any;
  loading?: boolean;
  response?: PromotionV2 | null | undefined;
};

export interface AvailablePricingPlans {
  response?: Maybe<Array<Maybe<PricingPlan>>>;
  loading?: boolean;
}

export interface EntitlementReactivationPaneProps {
  entitlement: EntitlementDetails;
  transactionAccount: Maybe<TransactionAccount>;
  showField: (arg: boolean) => unknown;
  isFieldDisable: boolean;
  creditDetails?: EntitlementTermDetails | null | undefined;
  setIsFieldDisable: React.Dispatch<React.SetStateAction<boolean>>;
  productSkipTrialEligible: boolean;
  updateLinkedEntitlements?: (data: any) => void;
  setLoading?: (arg: boolean) => void;
  linkedEntitlementsData?: LinkedDestinationEntitlement[];
  isBillEsimationEnabled?: boolean;
  formValueRef?: any;
  setEstimatedBill?: (arg: Estimate) => void;
  estimatedBill: Estimate;
  setFieldValue?: (name: string, value: unknown) => void;
  loadingLinkedEntitlement?: boolean;
  formState: FormState;
  updateFormState: (data: FormState) => void;
  isReactivationDisabled: boolean;
  setIsReactivationDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  termPaymentValidation: TermPaymentValidation;
  setTermPaymentValidation: React.Dispatch<React.SetStateAction<TermPaymentValidation>>;
  constructRequestData: (entitlementId: string, formData: FormData) => CustomOrderRequest | UpdateOrderRequest;
  setGrandTotal: (value: number) => void;
}

const getUnitName = (productId?: string | null, billingFrequency?: string | null) => {
  if (productId && isStatuspage(productId)) return 'Units';
  if (productId && isJSMProduct(productId)) {
    //add for bamboo product check as well
    return 'Agents';
  }
  if (billingFrequency && billingFrequency === BillingFrequency.ANNUAL) return 'User tier';
  if (productId && isAtlassianAccess(productId)) return 'Billable users';
  return 'Users';
};

export const currentDate = moment.utc(new Date());

export const EntitlementReactivationPaneComponent = memo(
  ({
    entitlement,
    transactionAccount,
    showField,
    isFieldDisable,
    creditDetails,
    setIsFieldDisable,
    productSkipTrialEligible,
    updateLinkedEntitlements,
    setLoading,
    linkedEntitlementsData,
    isBillEsimationEnabled,
    formValueRef,
    setEstimatedBill,
    estimatedBill,
    setFieldValue,
    loadingLinkedEntitlement,
    formState,
    updateFormState,
    constructRequestData,
    isReactivationDisabled,
    setIsReactivationDisabled,
    termPaymentValidation,
    setTermPaymentValidation,
    setGrandTotal,
  }: EntitlementReactivationPaneProps) => {
    const componentMounted = useRef(true);
    const [isEnabled] = useFeatureToggle();
    // FIXME :: should be based on a entitlement key
    const isFreePlan = isFreePlanEntitlement(entitlement);
    const [billingFrequency, setBillingFrequency] = useState<string>(
      entitlement?.billingFrequency || BillingFrequency.MONTHLY
    );
    const [partnerPromotionData, setPartnerPromotionData] = useState<PartnerPromotionType>({});

    const isAnnualTermEnabled = isEnabled(ENABLE_ANNUAL_TERMS);
    const isCreditManagementEnabled = isEnabled(ENABLE_CREDIT_MANAGEMENT);
    const [canUpdatePO, setCanUpdatePO] = useState<boolean>(false);
    const [displayUnsupportedBillingProfileBanner, setDisplayUnsupportedBillingProfileBanner] = useState<boolean>(
      false
    );
    const [availableOfferingsData, setAvailableOfferingsData] = useState<Partial<QueryResult>>({ loading: true });
    const [availableOfferings, setAvailableOfferings] = useState([]);
    const [offering, setOffering] = useState([]);
    const [isDisableBillingFrequencySelect, setIsDisableBillingFrequencySelect] = useState<boolean>(false);
    const isEntRelationFeatureEnable = isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE);
    const [selectedPricingPlan, setSelectedPricingPlan] = useState<PricingPlan>();
    const [defaultApiData, setDefaultApiData] = React.useState<Partial<BillingAnchorResponse>>({
      loading: isAnnualTermEnabled ? false : true,
    });
    const [availablePricings, setAvailablePricings] = useState<AvailablePricingPlans>({ loading: true });
    const formData = formValueRef?.current() && formValueRef?.current()?.values;
    const isFreeOffering = isFreeOfferingEntitlement(entitlement);
    const isPaidOffering = !isFreeOffering && entitlementHasDefaultPricingPlans(entitlement);
    const [isLinkedEntitlementDataLoading, setIsLinkedEntitlementDataLoading] = useState<boolean>(true);
    const [loadingData, setLoadingData] = useState<boolean>(true);

    // if the subscription is of free trial, we set default value as true, for paid subscription user can make selection
    const [isSkipTrialChecked, setIsSkipTrialChecked] = useState<boolean>(isFreeOffering);
    const isBulkEditEnabled = isEnabled(ENABLE_MANAGE_SUBSCRIPTION_BULK_EDIT);
    const isPartnerPromotionEnabled = isEnabled(ENABLE_PARTNER_PROMOTION);

    let purchaseFor = 'Monthly';
    if (billingFrequency === BillingFrequency.ANNUAL) {
      purchaseFor = 'Auto renew';
      if (formState?.endTimestamp) {
        purchaseFor = 'Termed';
      }
    }

    const constructFormState = (formData: any) => {
      const formState = {
        billingFrequency: formData?.billingFrequency,
        offering: {
          label: formData?.offeringSelectedItem?.label,
          value: formData?.offeringSelectedItem?.value?.id,
        },
        userTier: {
          value: formData?.offeringQuantity,
          label: '',
        },
        units: formData?.offeringQuantity,
        skipTrial: formData?.skipTrial,
        pricingPlan: {
          label: formData?.pricePlanName,
          value: '',
        },
        skipTrialEligible: productSkipTrialEligible,
      };
      return formState;
    };

    useEffect(() => {
      if (!isStatuspage(entitlement?.productId)) {
        setIsDisableBillingFrequencySelect(true);
      }
    }, []);

    const checkFinancePolicy = async (formData: FormData) => {
      setTermPaymentValidation({
        loading: true,
        response: { status: termPaymentValidation.response?.status || txaPushPaymentStatus.INELIGIBLE },
      });
      setIsReactivationDisabled(false);
      try {
        const financePayload = constructRequestData(entitlement?.id, formData);
        const processedFinancePayload = omit(financePayload, ['cloudSiteId', 'isDeleted', 'isPOAEnabled']);
        const termPaymentResponse = await termPaymentVerification(
          processedFinancePayload as CustomOrderRequest | UpdateOrderRequest
        );
        if (termPaymentResponse?.status === txaPushPaymentStatus.INELIGIBLE) {
          if (termPaymentResponse?.reasonCode === FinancePolicyReasonCode.ERR_POLICY_ORCH_003) {
            setDisplayUnsupportedBillingProfileBanner(true);
          }
          setTermPaymentValidation({
            loading: false,
            response: { status: termPaymentValidation.response?.status as txaPushPaymentStatus },
          });
          setIsReactivationDisabled(true);
        }
      } catch (e) {
        setTermPaymentValidation({
          loading: false,
          response: { status: termPaymentValidation.response?.status as txaPushPaymentStatus },
        });
      }
    };

    const linkedEntsTableVisible = useMemo(() => {
      if (isEntRelationFeatureEnable) {
        if (isStatuspage(entitlement?.productId)) {
          return true;
        }
        return !!entitlement?.relatesFromEntitlements?.length || !!entitlement?.relatesToEntitlements?.length;
      }
    }, [entitlement, isEntRelationFeatureEnable]);

    useEffect(() => {
      if (linkedEntsTableVisible && setLoading) {
        setLoading(true);
      } else {
        isAnnualTermEnabled && setIsLinkedEntitlementDataLoading(false);
      }
    }, [linkedEntsTableVisible, setLoading]);

    // checks linkedEntitlement billingFrequency and toggles billingFrequency switch
    useEffect(() => {
      if (isAnnualTermEnabled) return;
      const activeEntitlement = linkedEntitlementsData?.find((entitlementData) => entitlementData.billingFrequency);

      // annual entitlment only
      if (
        activeEntitlement?.billingFrequency === BillingFrequency.ANNUAL &&
        entitlement?.billingFrequency === BillingFrequency.MONTHLY
      ) {
        setIsDisableBillingFrequencySelect(true);

        setBillingFrequency(BillingFrequency.ANNUAL); // set billingFrequency toggle value and defaultCheck on Annual
        setUnitFieldName(getUnitName(entitlement?.productId, BillingFrequency.ANNUAL));
      }
    }, [linkedEntitlementsData]);

    const setPartnerPromotion = async (skipTrial?: boolean) => {
      if (isPartnerPromotionEnabled) {
        const formValues = formValueRef.current && formValueRef.current().values;
        const intendedBillingPeriodState = getIntendedBillingPeriodState(skipTrial);
        try {
          setPartnerPromotionData({
            error: null,
            loading: true,
          });
          const partnerPromotionResponse = await getPartnerPromotionQuery({
            entitlementNumber: entitlement.entitlementNumber,
            intendedBillingPeriodState,
            billingFrequency: formValues?.billingFrequency || entitlement.billingFrequency,
            discountTier: transactionAccount?.partnerDiscountTier,
            programLevelGlobal: transactionAccount?.programLevelGlobal,
          });
          const partnerPromotion = partnerPromotionResponse?.data?.entitlementDetails?.availablePartnerPromotions?.[0];
          setPartnerPromotionData((partnerPromotionData) => ({
            ...partnerPromotionData,
            response: partnerPromotion ?? null,
          }));
        } catch (error) {
          // TODO: handle error case
          setPartnerPromotionData((partnerPromotionData) => ({
            ...partnerPromotionData,
            error,
          }));
        } finally {
          setPartnerPromotionData((partnerPromotionData) => ({
            ...partnerPromotionData,
            loading: false,
          }));
        }
      }
    };

    useEffect(() => {
      setPartnerPromotion(isSkipTrialChecked);
      // isSkipTrialChecked is not added here as a dependency
      // instead setPartnerPromotion is called on check-uncheck of skipTrial directly
      // this will switch the partnerPromotionData.loading=true and isSkipTrialChecked at the same update stopping duplicate bill estimate API calls
    }, [billingFrequency]);

    // checks linkedEntitlement billingFrequency and toggles billingFrequency switch
    // sets endTimestamp and nextBillingAnchorTimestamp
    useEffect(() => {
      if (isAnnualTermEnabled && !isFreePlan && !isLinkedEntitlementDataLoading) {
        let currentEntitlementEndTimestamp;
        let currentEntitlementBillingAnchorTimestamp;
        const activeEntitlement = linkedEntitlementsData?.find((entitlementData) => entitlementData.billingFrequency);
        if (activeEntitlement && !isStatuspage(entitlement?.productId)) {
          if (activeEntitlement?.nextBillingTimestamp) {
            currentEntitlementBillingAnchorTimestamp = activeEntitlement.nextBillingTimestamp;
          }
          if (activeEntitlement?.billingFrequency === BillingFrequency.ANNUAL) {
            if (activeEntitlement?.endTimestamp) {
              currentEntitlementEndTimestamp = activeEntitlement.endTimestamp;
            }

            // switch from Monthly to Annual
            if (entitlement?.billingFrequency === BillingFrequency.MONTHLY) {
              setIsDisableBillingFrequencySelect(true);
              setBillingFrequency(BillingFrequency.ANNUAL); // set billingFrequency toggle value and defaultCheck on Annual
              setUnitFieldName(getUnitName(entitlement?.productId, BillingFrequency.ANNUAL));
            }
          }
        } else {
          currentEntitlementBillingAnchorTimestamp = moment.utc(currentDate).valueOf();
          if (entitlement?.billingFrequency === BillingFrequency.ANNUAL && !isStatuspage(entitlement?.productId)) {
            currentEntitlementBillingAnchorTimestamp = moment
              .utc(getCorrectedCurrentDateForAnnual(currentDate))
              .valueOf();
            if (entitlement?.endTimestamp) {
              currentEntitlementEndTimestamp = moment
                .utc(getCorrectedCurrentDateForAnnual(currentDate))
                .add(12, 'months')
                .valueOf();
            }
          }
        }
        updateFormState({
          ...formState,
          endTimestamp: currentEntitlementEndTimestamp,
          billingAnchorTimestamp: currentEntitlementBillingAnchorTimestamp,
        });
        setLoadingData(false);
      }
    }, [isLinkedEntitlementDataLoading]);

    const shouldRenderNextBillDate =
      !isStatuspage(entitlement?.productId) && formState?.billingAnchorTimestamp && !formState?.endTimestamp;
    const [unitFieldName, setUnitFieldName] = useState(
      getUnitName(entitlement?.productId, entitlement?.billingFrequency)
    );

    const currentInvoiceGroup = useMemo(() => {
      return ((transactionAccount?.availableInvoiceGroups?.nodes ?? []) as InvoiceGroup[])?.find(
        (invoiceGroup: InvoiceGroup) => {
          return invoiceGroup.id === entitlement.billingGroupId;
        }
      );
    }, [entitlement, transactionAccount]);

    const isViewPricingDetailsEnabled = isEnabled(ENABLE_VIEW_PRICING_DETAILS_ON_MANAGE);
    const isChangePricingEnabled = isEnabled(ENABLE_PRICING_PLAN_CHANGE);
    const isBillingCycleChanged = !isFreePlan && entitlement?.billingFrequency !== billingFrequency;

    const getDefaultAPIAndPricingPlanData = async () => {
      const promises = [
        fetchPricingPlans({
          entitlementNumber: entitlement.entitlementNumber,
          currency: defaultTo(currentInvoiceGroup?.currency, Currency.USD),
          offeringId: defaultTo(entitlement?.offeringId, ''),
          planType: entitlement?.pricingPlan?.type,
          billingCycle: billingFrequency as BillingFrequency,
        }),
      ];

      if (!isAnnualTermEnabled) {
        promises.push(
          //@ts-ignore
          getDefaultAPIEntitlementData({
            entitlement,
            entitlementId: entitlement.id,
            offeringId: entitlement.offeringId,
            billingCycle: billingFrequency as BillingFrequency,
          })
        );
      }

      try {
        const result: any = await Promise.all(promises);
        const pricingPlansResponse = result[0] as ApolloQueryResult<FetchAvailablePricingPlansQuery>;
        const pricingPlans = pricingPlansResponse?.data?.entitlementDetails?.availablePricingPlans;
        setAvailablePricings({ response: pricingPlans as PricingPlan[], loading: false });

        if (!isAnnualTermEnabled) {
          const defaultApiResponse = result[1] as EntitlementDefaultsResponse;
          setDefaultApiData({ response: defaultApiResponse, loading: false });
        }
      } catch (error) {
        !isAnnualTermEnabled && setDefaultApiData({ error, loading: false });
      }
    };

    useEffect(() => {
      !isAnnualTermEnabled && setDefaultApiData({ loading: true });
      setAvailablePricings({ loading: true });
      getDefaultAPIAndPricingPlanData();
    }, [isBillingCycleChanged]);

    const defaultBillingGroupId = entitlement?.activeSubscriptionBillingGroupId ?? entitlement?.billingGroupId;
    const availableInvoiceGroups: Maybe<InvoiceGroup>[] | undefined = useMemo(() => {
      return isFreePlan || !transactionAccount?.availableInvoiceGroups?.nodes
        ? []
        : transactionAccount?.availableInvoiceGroups?.nodes;
    }, [isFreePlan, transactionAccount?.availableInvoiceGroups?.nodes]);

    const linkedEntitlementInvoiceGroupId = useMemo(() => {
      return linkedEntitlementsData?.find((entitlement: LinkedDestinationEntitlement) => !!entitlement?.billingGroupId)
        ?.billingGroupId;
    }, [linkedEntitlementsData]);
    const [availableFilteredInvoiceGroups, defaultInvoiceGroup] = useMemo(() => {
      const filteredInvoiceGroups = linkedEntitlementInvoiceGroupId
        ? availableInvoiceGroups?.filter((invoiceGroup) => invoiceGroup?.id === linkedEntitlementInvoiceGroupId)
        : availableInvoiceGroups;
      return [
        filteredInvoiceGroups,
        linkedEntitlementInvoiceGroupId ? filteredInvoiceGroups?.[0] : currentInvoiceGroup || null,
      ];
    }, [linkedEntitlementInvoiceGroupId, availableInvoiceGroups, currentInvoiceGroup]);

    const [invoiceGroup, setInvoiceGroup] = useState<Maybe<InvoiceGroup>>(defaultInvoiceGroup);

    const [offeringQuantity, setOfferingQuantity] = useState(entitlement?.unitCount ?? '1');

    const scheduledChanges = entitlement?.scheduledChanges ?? null;

    useEffect(() => {
      if (!loadingLinkedEntitlement && !availablePricings.loading && !defaultApiData?.loading) {
        // This function is used to determine the offering quantity of an entitlement
        let chargeQuantity;
        if (billingFrequency === BillingFrequency.ANNUAL && !isStatuspage(entitlement?.productId)) {
          if (entitlement?.isApp) {
            // If the entitlement is app and annual billing frequency, then userTier needs to be calculated based on its parent
            chargeQuantity = getProductMaxUserTier(linkedEntitlementsData)?.toString();

            if (!chargeQuantity) {
              const userTierOptions = getUserTierOptions({
                activeUsers: entitlement.activeUsers,
                userTiers: entitlement.pricingPlan?.tiers,
              });
              chargeQuantity = userTierOptions?.[0]?.value?.toString();
            }
          } else if (isBillingCycleChanged) {
            // For products switching from Monthly to Annual apart from Apps
            let annualPricingPlan;
            if (isAnnualTermEnabled) {
              annualPricingPlan = getDefaultPricingPlan(availablePricings?.response);
            } else {
              annualPricingPlan = availablePricings?.response?.find(
                (pricingPlanObj) => pricingPlanObj?.id === defaultApiData?.response?.pricingPlanId?.value
              );
            }
            // const highestUserTier = getProductMaxUserTier(linkedEntitlementsData) ?? 0;

            // let users = highestUserTier;
            // if (highestUserTier === 0 || (entitlement?.totalBilledUsers ?? 0) < highestUserTier) {
            //   users = entitlement?.totalBilledUsers ?? 0;
            // }
            const userTierOptions = getUserTierOptions({
              //   activeUsers: users,
              activeUsers: entitlement.activeUsers,
              userTiers: annualPricingPlan?.tiers,
            });
            chargeQuantity = userTierOptions?.[0]?.value?.toString();
          } else {
            // For products which are already in annual cycle
            chargeQuantity = getUserTier(entitlement?.subscription)?.toString();
          }
        } else {
          chargeQuantity = entitlement?.scheduledChanges?.chargeQuantities?.[0]?.quantity?.toString() ?? null;
        }

        if (!chargeQuantity) {
          chargeQuantity = entitlement?.unitCount ?? '1';
        }
        setOfferingQuantity(chargeQuantity);
        setFieldValue?.('offeringQuantity', chargeQuantity);
      }
    }, [
      entitlement,
      billingFrequency,
      linkedEntitlementsData,
      loadingLinkedEntitlement,
      availablePricings,
      defaultApiData,
    ]);

    useEffect(() => {
      const loadAvailableOfferings = async (): Promise<void> => {
        const offerings = await graphqlClient().query({
          query: fetchAvailableOfferingsForEntitlement,
          variables: { id: entitlement.id },
          fetchPolicy: 'no-cache',
        });

        setAvailableOfferingsData({ ...offerings });
      };

      showField(false);
      loadAvailableOfferings();
    }, []);

    useEffect(() => {
      if (availableOfferingsData?.data && !availableOfferingsData?.loading) {
        const {
          entitlementDetails: { availableOfferings = [], offering },
        } = availableOfferingsData.data;

        setAvailableOfferings(availableOfferings);
        setOffering(offering);
        showField(true);
      }
    }, [availableOfferingsData.data, availableOfferingsData?.loading, showField]);

    useEffect(() => () => {
      // Runs when component is unmounted
      componentMounted.current = false;
    });

    useEffect(() => {
      if (isAnnualTermEnabled) return;
      const formValues = formValueRef.current() && formValueRef.current().values;
      if (
        !loadingLinkedEntitlement &&
        !availablePricings?.loading &&
        !defaultApiData?.loading &&
        !partnerPromotionData?.loading &&
        isBillEsimationEnabled &&
        formValues.offeringSelectedItem &&
        formValues.invoiceGroupId
      ) {
        triggerBillEstimate();
      }
    }, [
      loadingLinkedEntitlement,
      availableOfferings,
      invoiceGroup,
      billingFrequency,
      selectedPricingPlan,
      isSkipTrialChecked,
      offeringQuantity,
      partnerPromotionData,
    ]);

    useEffect(() => {
      if (isAnnualTermEnabled) {
        const formValues = formValueRef.current() && formValueRef.current().values;
        if (
          !isLinkedEntitlementDataLoading &&
          !loadingData &&
          !availablePricings?.loading &&
          !defaultApiData?.loading &&
          !partnerPromotionData?.loading &&
          isBillEsimationEnabled &&
          formValues.offeringSelectedItem &&
          formValues.invoiceGroupId
        ) {
          triggerBillEstimate();
        }
      }
    }, [
      isLinkedEntitlementDataLoading,
      loadingData,
      availablePricings?.loading,
      invoiceGroup,
      isSkipTrialChecked,
      partnerPromotionData,
    ]);

    useEffect(() => {
      const paymentMethodType = defaultInvoiceGroup && defaultInvoiceGroup?.defaultPaymentMethod?.type;
      if (
        paymentMethodType === PaymentMethodType.DEFERRED &&
        isCreditManagementEnabled &&
        !isStatuspage(entitlement.productId) &&
        formState.endTimestamp
      ) {
        const formData = formValueRef?.current() && formValueRef?.current()?.values;
        checkFinancePolicy(formData);
      }
    }, [isSkipTrialChecked, defaultInvoiceGroup, formState]);

    const triggerBillEstimate = async (promotionValues?: any, customPromotionDetails?: PromotionItemDetail) => {
      const formValues = formValueRef.current && formValueRef.current().values;

      // Update form promotion values after removal of promotion
      if (promotionValues?.length > 0) {
        promotionValues?.forEach((promotion: any, index: number) => {
          formValues[`promotionId-${index}`] = promotion;
        });
        const removeItem = `promotionId-${promotionValues.length}`;
        delete formValues[removeItem];
      }

      let discountValue,
        percentageValue = 0;
      const promotionIds = [],
        customPromotions: PromotionInstance[] = [];
      const promotionKeys = Object.keys(formValues).filter((key) => key.trim().includes('promotionId'));
      for (let i = 0; i < promotionKeys.length; i++) {
        if (isDiscretionaryDiscountPromotion(formValues, promotionKeys[i])) {
          const customkey = `customPromotionId-${i}`;
          // @ts-ignore
          const customPromotionId = formValues[customkey]?.value;
          customPromotions.push(
            newAddedPromotionValueResolver(customPromotionId, formValues, i, customPromotionDetails)
          );
          percentageValue = defaultTo(Number(customPromotionDetails?.discountPercentage), 0);
        } else {
          const promotionObject: any = {
            promotionDefinition: {},
          };
          if (get(formValues, promotionKeys[i])?.promotion?.type === MARKETPLACE_PROMO_CODE) {
            promotionObject.promotionDefinition.promotionCode = formValues.promoCode;
          } else {
            promotionObject.promotionDefinition.promotionId = get(formValues, `${promotionKeys[i]}.value`, '');
          }
          if (promotionObject.promotionDefinition.promotionId || promotionObject.promotionDefinition.promotionCode) {
            promotionIds.push(promotionObject);
          }
        }
      }

      if (formValues?.partnerPromotion?.id) {
        promotionIds.push({
          promotionDefinition: {
            promotionId: formValues?.partnerPromotion?.id,
          },
        });
      }

      setEstimatedBill && setEstimatedBill({ loading: true });
      try {
        const request: OrderIntentRequest = {
          transactionAccountId: defaultTo(entitlement?.transactionAccountId, ''),
          invoiceGroupId: defaultTo(formValues?.invoiceGroupId?.value, ''),
          entitlementId: defaultTo(entitlement.id, ''),
          orderType: OrderItemType.REACTIVATION_ORDER,
          offeringId: defaultTo(formValues.offeringSelectedItem?.value.id, ''),
          units: formValues?.offeringQuantity,
          billingFrequency: formValues?.billingFrequency as PricingPlanCycle,
          planType:
            formValues?.pricePlan?.pricingPlanType ?? defaultTo(get(entitlement, 'pricingPlan.pricingPlanType'), null),
          promotionIds,
          customPromotions,
          skipTrial: isStatuspage(entitlement?.productId)
            ? undefined
            : Boolean(isSkipTrialChecked || !!formValues?.skipTrial),
        };
        if (formValues?.billingFrequency === BillingFrequency.ANNUAL && formState?.endTimestamp) {
          request.endsAt = {
            type: EndsAtType.TIMESTAMP,
            value: formState.endTimestamp,
          };
        }
        const response = await getBillEstimate(request);
        discountValue = Math.floor(defaultTo(Number(response?.discretionaryDiscountAmount), 0));
        setEstimatedBill &&
          setEstimatedBill({ response, value: discountValue, percentage: percentageValue, loading: false });
      } catch (err) {
        setEstimatedBill && setEstimatedBill({ loading: false, error: err });
      }
    };

    const handleBillEstimateAfterAddPromotion = async (item: any) => {
      if (item?.promotion?.type !== DISCRETIONARY_DISCOUNT) {
        triggerBillEstimate();
      }
    };

    const handleBillEstimateAfterRemovePromotion = async (values: any) => {
      // Trigger bill-estimate API with updated remove promotion values
      triggerBillEstimate(values);
    };

    const handleBillEstimateAfterCustomPromotionValueChange = async (item: any) => {
      const customPromotionDetails = item;

      // Trigger bill-estimate API with updated custom-promotion details
      triggerBillEstimate([], customPromotionDetails);
    };

    const handleShowPONumberSection = useCallback(() => {
      setCanUpdatePO(!canUpdatePO);
      setIsFieldDisable(true);
    }, [canUpdatePO, isFieldDisable]);

    if (availableOfferingsData.loading && !availableOfferingsData.data) return <LoadingIndicator />;
    if (!availableOfferingsData.data) {
      return null;
    }

    const availableOfferingOptions =
      availableOfferings?.map((availableOffering: Offering) => ({
        label: defaultTo(availableOffering.name, ''),
        value: {
          id: availableOffering.id,
        },
      })) ?? ([] as OfferingSelectItem[]);

    if (offering && availableOfferingOptions) {
      availableOfferingOptions.unshift({
        //@ts-ignore
        label: offering.name,
        value: {
          //@ts-ignore
          id: offering.id,
        },
      });
    }

    const promotions = (scheduledChanges?.promotions
      ? scheduledChanges?.promotions
      : entitlement?.promotions ?? []) as Maybe<Promotion[]>;

    const appropriateAvailablePromotions = getAppropriatePromotions(
      entitlement?.availablePromotions,
      entitlement?.billingFrequency
    );

    if (entitlement?.isApp) {
      appropriateAvailablePromotions.push({
        type: MARKETPLACE_PROMO_CODE,
        title: 'Promo code',
        status: 'ACTIVE',
      });
    }

    const appropriatePromotions = getAppropriatePromotions(promotions, entitlement?.billingFrequency);

    const defaultOfferingOption = availableOfferingOptions.find(
      (availableOffering: OfferingSelectItem) =>
        availableOffering.value.id ===
        (scheduledChanges?.offering ? scheduledChanges.offering?.id : entitlement.offering?.id)
    );

    const toggleState = (frequency?: string | null) => {
      const nextBillingFrequency: BillingFrequency =
        frequency === BillingFrequency.ANNUAL ? BillingFrequency.MONTHLY : BillingFrequency.ANNUAL;

      if (frequency) {
        setBillingFrequency(nextBillingFrequency);
        setUnitFieldName(getUnitName(entitlement?.productId, nextBillingFrequency));
      }
    };

    const handleInvoiceGroupChange: FieldProps<ValueType<InvoiceGroupSelectItem>, HTMLSelectElement>['onChange'] = (
      value
    ) => {
      // Use type narrowing because `value` can be either a selection option item object or the onChange `event`
      if (value && 'invoiceGroup' in value) {
        setInvoiceGroup(value.invoiceGroup);
      }
    };

    const renderInlineMessage = (dismissMessage: () => void) => (
      <InlineMessage
        dismissMessage={dismissMessage}
        headerText="View pricing details"
        labelText="For more information about this pice plan, go to product pricing portal."
        buttonText="View pricing details"
      />
    );

    const renderBillingAnchorDate = () => {
      const isAnnual = billingFrequency === PricingPlanCycle.ANNUAL;
      const anchorTime = get(defaultApiData, 'response.billingAnchorTime.value');
      return defaultApiData?.loading ? (
        <SkeletonLoaderWrapper>
          <SkeletonLoader classNameInner="billing-timestamp-loader" width="400px" />
        </SkeletonLoaderWrapper>
      ) : get(defaultApiData, 'response.billingAnchorTime.value') ? (
        <Field
          label=""
          name="nextBillingAnchorTimestamp"
          defaultValue={get(defaultApiData, 'response.billingAnchorTime.value')}
        >
          {() => (
            <div data-testid="billing-anchor-time">
              <InfoIcon testId="info-icon" label="Info" size="small" />{' '}
              <label>{`Bill anchor date will be set to the ${moment.utc(anchorTime).format('Do')} ${
                isAnnual ? moment.utc(anchorTime).format('MMMM') : ''
              } of every ${isAnnual ? 'year' : 'month'}.`}</label>
            </div>
          )}
        </Field>
      ) : null;
    };

    const renderSubscriptionDetailsTable = () => {
      return (
        <>
          <h2>Subscription details</h2>
          {isBulkEditEnabled ? (
            <ProductFamilyTableV2
              entitlement={entitlement}
              estimatedBill={estimatedBill}
              formState={constructFormState(formData)}
              isBillingCycleChanged={isBillingCycleChanged}
              isMonthlySelected={billingFrequency === BillingFrequency.MONTHLY}
              isLoading={loadingLinkedEntitlement}
            />
          ) : (
            <ProductFamilyTable
              entitlement={entitlement}
              billingCycle={(billingFrequency as unknown) as PricingPlanCycle}
              pricingPlan={selectedPricingPlan?.offeringName ?? entitlement?.offering?.name}
              userTier={formData?.offeringQuantity}
              estimatedBill={estimatedBill}
              isLoading={loadingLinkedEntitlement}
            />
          )}

          {isBillEsimationEnabled && (
            <PriceDifference
              currentEstimatedBill={entitlement?.nextBillEstimate?.total || 0}
              estimatedBill={estimatedBill}
              entitlement={entitlement}
              isBillingCycleChanged={isBillingCycleChanged}
              isLoading={loadingLinkedEntitlement}
              setGrandTotal={setGrandTotal}
            />
          )}
        </>
      );
    };

    return (
      <div className="reactivation-container">
        {!isAnnualTermEnabled && renderBillingAnchorDate()}
        {linkedEntsTableVisible && (
          <FormSection>
            <LinkedEntitlementsTable
              entitlementId={entitlement?.id}
              parentContainerID={getParentContainerId(entitlement)}
              currentEntitlementType={LinkedEntitlementTypes.PROD_ENTITLEMENT_APP}
              destinationEntitlementType={[LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT]}
              findProducts={true}
              findApps={false}
              productType={entitlement.productType}
              checkForLastParentExistence={false}
              message={'Bill date matches these subscriptions:'}
              updateLinkedEntitlements={updateLinkedEntitlements}
              setLoading={(data) => {
                setLoading && setLoading(data);
                isAnnualTermEnabled && setIsLinkedEntitlementDataLoading(data);
              }}
            />
          </FormSection>
        )}
        <FormSection>
          <Field<ValueType<OfferingSelectItem>, HTMLSelectElement>
            name="offeringSelectedItem"
            defaultValue={defaultOfferingOption}
            label="Plan"
          >
            {({ fieldProps }) => (
              <div data-testid="product-offering-select" className="product-offering-select">
                <Select<OfferingSelectItem>
                  {...fieldProps}
                  options={availableOfferingOptions}
                  classNamePrefix="offering-select"
                  testId="product-offering-details-options"
                  isDisabled={true}
                />
              </div>
            )}
          </Field>
        </FormSection>

        {isEnabled(ENABLE_ENTITLEMENT_SKIP_TRIAL) && productSkipTrialEligible && (
          <>
            <FormSection>
              <Field label="Skip trial" name="skipTrial">
                {({ fieldProps }) => (
                  <Checkbox
                    {...fieldProps}
                    defaultChecked={isSkipTrialChecked}
                    isChecked={isSkipTrialChecked}
                    isDisabled={!isPaidOffering}
                    onChange={(event: FormEvent<HTMLInputElement>) => {
                      setPartnerPromotion(event.currentTarget.checked);
                      setIsSkipTrialChecked(event.currentTarget.checked);
                      fieldProps.onChange(event);
                    }}
                    testId="skipTrial"
                  />
                )}
              </Field>
            </FormSection>
          </>
        )}

        <FormSection>
          <Field name="billingFrequency" label="Billing cycle" defaultValue={billingFrequency}>
            {({ fieldProps }) => (
              <div className="toggler-container">
                <span className={billingFrequency === BillingFrequency.MONTHLY ? 'underline' : 'blur'}>Monthly</span>
                <Toggle
                  {...fieldProps}
                  isDisabled={isDisableBillingFrequencySelect}
                  name="billingFrequency"
                  testId="frequency-toggle"
                  value={billingFrequency}
                  onChange={() => {
                    toggleState(billingFrequency);
                    fieldProps.onChange(
                      billingFrequency === BillingFrequency.ANNUAL ? BillingFrequency.MONTHLY : BillingFrequency.ANNUAL
                    );
                  }}
                  isChecked={billingFrequency === BillingFrequency.ANNUAL}
                />
                <span className={billingFrequency === BillingFrequency.ANNUAL ? 'underline' : 'blur'}>Annual</span>
              </div>
            )}
          </Field>
        </FormSection>

        <FormSection>
          <Field name="offeringQuantity" label={unitFieldName} defaultValue={offeringQuantity}>
            {({ fieldProps }) => (
              <Textfield {...fieldProps} testId="offering-quantity" width={80} value={offeringQuantity} isDisabled />
            )}
          </Field>
        </FormSection>

        {isChangePricingEnabled && !isFreePlan ? (
          <ChangePricePlan
            entitlement={entitlement}
            isManagePane={!isFieldDisable && billingFrequency !== BillingFrequency.ANNUAL}
            availablePricingPlans={availablePricings.response as PricingPlan[]}
            planId={
              isBillingCycleChanged
                ? isAnnualTermEnabled
                  ? getDefaultPricingPlan(availablePricings?.response)?.id
                  : defaultApiData?.response?.pricingPlanId?.value
                : entitlement?.pricingPlan?.id
            }
            setSelectedPricingPlan={setSelectedPricingPlan}
          />
        ) : (
          <FormSection>
            <Field
              name="pricing"
              label="Pricing"
              defaultValue={entitlement.pricingPlan?.type || PricingPlanType.COMMERCIAL}
            >
              {() => (
                <div className="elem-with-tooltip">
                  <span>{capitalize(defaultTo(entitlement.pricingPlan?.type, 'Not Applicable'))}</span>
                  {isViewPricingDetailsEnabled && (
                    <DismissableInlineMessage
                      position="right"
                      renderMessage={(dismissMessage) => renderInlineMessage(dismissMessage)}
                      renderTitle={(toggleMessage) => (
                        <span className="info-icon" onClick={toggleMessage}>
                          <InfoIcon testId="info-icon" label="Info" size="small" />
                        </span>
                      )}
                    />
                  )}
                </div>
              )}
            </Field>
          </FormSection>
        )}

        <FormSection>
          {partnerPromotionData?.response && !isFreePlan && (
            <div className="partner-promotion-section">
              <h3>
                <span className="partner-promotion-header">Discounts to be applied to next bill</span>
              </h3>
              <div data-testid="partnerPromotionBox">
                <PartnerPromotionItem partnerPromotion={partnerPromotionData?.response} />
              </div>
            </div>
          )}
          <SelectPromotionFormFieldV2
            availablePromotions={appropriateAvailablePromotions}
            promotions={appropriatePromotions}
            fieldLabel="Discount"
            canAddMultiplePromotions={true}
            productId={entitlement?.productId}
            entitlementId={entitlement?.id}
            isDisabled={isFieldDisable}
            onCustomValueChange={handleBillEstimateAfterCustomPromotionValueChange}
            onAfterAddPromotion={handleBillEstimateAfterAddPromotion}
            onAfterRemovePromotion={handleBillEstimateAfterRemovePromotion}
          />
        </FormSection>

        {isAnnualTermEnabled ? (
          <>
            {isLinkedEntitlementDataLoading ? (
              <div className="billing-details-loader">
                <LoadingIndicator />
              </div>
            ) : (
              <>
                <h2>Billing cycle details</h2>
                <div className="billing-details">
                  <SummarySectionV2.SummarySection
                    testId="summary"
                    className="summary-section-wrapper"
                    columns="repeat(auto-fit, minmax(120px, 1fr))"
                  >
                    <SummarySectionV2.SummaryGridCalloutItem>
                      <div>
                        <label>Purchase</label>
                        <div>{purchaseFor}</div>
                      </div>
                    </SummarySectionV2.SummaryGridCalloutItem>
                    {shouldRenderNextBillDate && (
                      <SummarySectionV2.SummaryGridCalloutItem>
                        <div>
                          <label>Bill anchor date</label>
                          <div>
                            {moment
                              .utc(formState.billingAnchorTimestamp)
                              .format(billingFrequency === BillingFrequency.MONTHLY ? 'DD' : 'MMM DD')}
                          </div>
                        </div>
                      </SummarySectionV2.SummaryGridCalloutItem>
                    )}
                    <SummarySectionV2.SummaryGridCalloutItem>
                      <div>
                        <label>Start date</label>
                        <div>
                          <FormattedDate date={moment.utc(currentDate).valueOf().toString()} format={NEW_DATE_FORMAT} />
                        </div>
                      </div>
                    </SummarySectionV2.SummaryGridCalloutItem>
                    {formState?.endTimestamp && (
                      <SummarySectionV2.SummaryGridCalloutItem>
                        <div>
                          <label>End date</label>
                          <div>
                            <FormattedDate date={formState.endTimestamp.toString()} format={NEW_DATE_FORMAT} />
                          </div>
                        </div>
                      </SummarySectionV2.SummaryGridCalloutItem>
                    )}
                  </SummarySectionV2.SummarySection>
                </div>
                {renderSubscriptionDetailsTable()}
              </>
            )}
          </>
        ) : (
          renderSubscriptionDetailsTable()
        )}

        {displayUnsupportedBillingProfileBanner && (
          <SectionMessage appearance="error" title="Unsupported Billing Profile">
            The credit limit for the selected billing profile is insufficient. Please let the customer know that they’re
            unable to proceed with the payment method associated with this billing profile.
          </SectionMessage>
        )}

        <FormSection>
          <InvoiceGroupFormField
            fieldLabel="Billing profile"
            testId="billing-profile-form-field"
            productId={entitlement?.productId ?? undefined}
            availableInvoiceGroups={availableInvoiceGroups as InvoiceGroup[]}
            creditDetails={creditDetails}
            entitlementBillingInvoiceGroupId={defaultBillingGroupId}
            scheduledBillingInvoiceGroupId={scheduledChanges?.scheduledBillingInvoiceGroupId}
            onChange={handleInvoiceGroupChange}
            isDisabled={true}
            isRequired={!isFreePlan && isSkipTrialChecked}
          />
        </FormSection>
        {isReactivationDisabled && <ErrorMessage>This billing profile is not supported</ErrorMessage>}

        {!canUpdatePO && (
          <FormSection>
            <Field name="updatePONumber" label="">
              {() => (
                <>
                  <Button
                    spacing="compact"
                    appearance="link"
                    onClick={handleShowPONumberSection}
                    iconBefore={<EditorAddIcon label="Star icon" size="small" />}
                    testId="visible-po-number-fields"
                  >
                    Update PO Number
                  </Button>
                </>
              )}
            </Field>
          </FormSection>
        )}

        {canUpdatePO && (
          <POUpdate
            setCanUpdatePO={setCanUpdatePO}
            entitlement={entitlement}
            transactionAccount={transactionAccount}
            setIsFieldDisable={setIsFieldDisable}
            shipToPartyId={invoiceGroup?.shipToParty?.id}
          />
        )}
      </div>
    );
  }
);

const POUpdate = ({
  setCanUpdatePO,
  entitlement,
  transactionAccount,
  setIsFieldDisable,
  shipToPartyId,
}: {
  setCanUpdatePO: React.Dispatch<React.SetStateAction<boolean>>;
  entitlement: EntitlementDetails;
  transactionAccount: Maybe<TransactionAccount>;
  setIsFieldDisable: React.Dispatch<React.SetStateAction<boolean>>;
  shipToPartyId?: string | null;
}) => {
  const [purchaseOrder, setPurchaseOrder] = React.useState<string>('');
  const [isMultiPO, setIsMultiPO] = useState<boolean>(false);
  const [isPOUpdated, setIsPOUpdated] = React.useState<Partial<PORequest>>({
    data: null,
    loading: false,
    done: false,
    error: null,
  });

  const onChangePurchaseOrder = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsPOUpdated({ loading: false, data: null, done: false, error: null });
    setPurchaseOrder(event.target.value);
  };

  const handleUpdatePONumber = async () => {
    if (!purchaseOrder) return;
    try {
      setIsPOUpdated((prevState) => ({ ...prevState, loading: true }));
      const requestPayload = {
        transactionAccountId: transactionAccount?.id || '',
        invoiceGroupId: entitlement?.billingGroupId || '',
        data: {
          shipToPartyId,
          purchaseOrder: {
            oneTimeUse: !isMultiPO,
            number: purchaseOrder,
          },
        },
      };
      const response = await updateInvoiceGroup(requestPayload);
      setIsPOUpdated({ loading: false, data: response, done: true, error: null });
      setIsFieldDisable(false);
    } catch (error) {
      setIsPOUpdated({ loading: false, data: null, done: false, error });
    }
  };

  return (
    <FormSection>
      <Field name="updatePONumber" label="PO Number">
        {({ fieldProps }) => (
          <>
            <Textfield
              {...fieldProps}
              name="updatePONumber"
              testId="update-po-number"
              placeholder="Add PO number"
              value={purchaseOrder}
              onChange={onChangePurchaseOrder}
            />

            <div className="elem-with-tooltip multi-po-container info-icon-wrapper">
              <Checkbox
                isChecked={isMultiPO}
                onChange={() => setIsMultiPO(!isMultiPO)}
                label="Multi-use PO number"
                value="Multi-use PO number"
                name="Multi-use PO number"
                testId="Multi-use PO number"
                alt="multi-use PO number"
              />
              <Tooltip content="Selecting the multi-use option means the purchase order number will be reflected in all subsequent invoices">
                <InfoIcon label="more-information" size="small" />
              </Tooltip>
            </div>

            {isPOUpdated.error !== null && (
              <div className="error-message-continer">
                <SectionMessage appearance="error">
                  <strong>The PO number didn’t update - try again.</strong>
                </SectionMessage>
              </div>
            )}
            <div className="btn-wrapper end-align">
              <Button
                appearance="link"
                onClick={() => {
                  setCanUpdatePO(false);
                  setIsFieldDisable(false);
                }}
                className="sidebar-btn-mr"
                testId="btn-close-po-section"
              >
                Undo
              </Button>
              <LoadingButton
                aria-label="Update PO Number"
                appearance="primary"
                onClick={!isPOUpdated.done ? handleUpdatePONumber : undefined}
                isLoading={isPOUpdated.loading}
                iconBefore={isPOUpdated.done ? <CheckIcon label="updated" size="medium" /> : undefined}
                isSelected={isPOUpdated.done}
                testId="btn-update-po-number"
              >
                {isPOUpdated.done ? 'Updated' : 'Update'}
              </LoadingButton>
            </div>
          </>
        )}
      </Field>
    </FormSection>
  );
};

export const EntitlementReactivationPane = withApollo(EntitlementReactivationPaneComponent);
